import React, { memo, useCallback, useState } from "react";
import { ChevronDown } from "lucide-react";
import VideoWithFallback from "./VideoWithFallback";
import { withDisabledHint } from "./withDisabledHint";

// Types
interface HeroSectionProps {
    scrollParentId: string;
    videoSrc: string;
    title: string;
    subtitle: string;
    imageSrc?: string;
    imageAlt?: string;
    buttonText?: string;
    onButtonClick?: () => void;
    // When set, the button is disabled and this explains why on hover.
    disabledReason?: string;
    titleClassName?: string;
    subtitleClassName?: string;
    imageClassName?: string;
    buttonClassName?: string;
    overlay?: boolean;

    // NEW
    fallbackImage?: string;
}

const scrollToSection = (sectionId: string): void => {
    const section = document.getElementById(sectionId);
    if (!section) {
        return;
    }

    const headerHeight =
        document.querySelector<HTMLElement>(".public-header")?.offsetHeight ?? 0;

    section.style.scrollMarginTop = `${headerHeight}px`;
    section.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Reusable Hero Section Component
const HeroSection: React.FC<HeroSectionProps> = memo(({
    scrollParentId,
    videoSrc,
    title,
    subtitle,
    imageSrc,
    imageAlt = "Hero Image",
    buttonText = "Enter",
    onButtonClick,
    disabledReason,
    overlay = false,
    fallbackImage,
    titleClassName = "page-title mb-2",
    subtitleClassName = "page-sub-title",
    imageClassName = "w-150px",
    buttonClassName = "btn bg-white bg-opacity-20 text-white btn-sm fs-6 mt-2 enter-button d-flex align-items-center gap-1"
}) => {

    const handleClick = useCallback(() => {
        onButtonClick?.();
    }, [onButtonClick]);

    const handleScrollDown = useCallback(() => {
        if (!scrollParentId) {
            return;
        }

        scrollToSection(scrollParentId);
    }, [scrollParentId]);

    return (
        <div className="vh-100 position-relative overflow-hidden">
            {/* Fallback Image */}
            <VideoWithFallback
                videoSrc={videoSrc}
                fallbackSrc={fallbackImage || ""}
            />
            {overlay && (
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark opacity-50" />
            )}

            <div className="position-absolute top-50 start-50 translate-middle text-center text-white w-100">
                <p className={titleClassName}>{title}</p>
                <p className={subtitleClassName}>{subtitle}</p>

                <div className="d-flex flex-column align-items-center">
                    {imageSrc && (
                        <img
                            src={imageSrc}
                            alt={imageAlt}
                            className={imageClassName}
                            loading="eager"
                        />
                    )}

                    {onButtonClick && withDisabledHint(
                        <button
                            className={`${buttonClassName} ${disabledReason ? 'pe-none' : ''}`}
                            aria-label={buttonText}
                            onClick={handleClick}
                            disabled={Boolean(disabledReason)}
                        >
                            <span className="enter-text px-4">
                                {buttonText}
                            </span>
                        </button>,
                        disabledReason
                    )}

                    <button
                        type="button"
                        className="hero-scroll-indicator btn btn-link d-flex flex-column align-items-center gap-1 p-0 border-0 text-decoration-none"
                        aria-label="Scroll to explore"
                        onClick={handleScrollDown}
                    >
                        <ChevronDown
                            size={22}
                            strokeWidth={1.75}
                            className="hero-scroll-chevron"
                            aria-hidden="true"
                        />
                        <span className="hero-scroll-label">Scroll to explore</span>
                    </button>
                </div>
            </div>
        </div>
    );
});

export default HeroSection;